import { z } from 'zod';

/**
 * Buy Box wie lib_buybox.php der PHP-Middleware: Bieten mehrere Händler denselben Artikel (gleiche EAN) an,
 * entscheidet die Regel je Größe, welches Angebot vorausgewählt ist.
 */
export const BUYBOX_STRATEGIEN = {
  preis: 'Bester Preis',
  bestand: 'Meister Bestand',
  bewertung: 'Beste Bewertung',
  lieferzeit: 'Schnellste Lieferung',
  score: 'Gewichteter Score',
} as const;
export type BuyBoxStrategie = keyof typeof BUYBOX_STRATEGIEN;
export const BUYBOX_STRATEGIE_LISTE = Object.keys(BUYBOX_STRATEGIEN) as BuyBoxStrategie[];

export const BUYBOX_GLEICHSTAND = {
  preis: 'Günstigerer Preis',
  bestand: 'Mehr Bestand',
  bewertung: 'Bessere Bewertung',
  lieferzeit: 'Schnellere Lieferung',
  aelter: 'Älteres Angebot',
} as const;
export type BuyBoxGleichstand = keyof typeof BUYBOX_GLEICHSTAND;

export const BUYBOX_BEWERTUNG_QUELLEN = {
  manuell: 'Manuell gepflegt',
  leistung: 'Aus Bestellungen (180 Tage)',
  kombiniert: 'Mittelwert aus beiden',
} as const;
export type BuyBoxBewertungQuelle = keyof typeof BUYBOX_BEWERTUNG_QUELLEN;

export const BUYBOX_EIGENES_ANGEBOT = {
  gleich: 'Gleichberechtigt',
  gleichstand: 'Bei Gleichstand bevorzugt',
  nie: 'Nie vorausgewählt',
} as const;
export type BuyBoxEigenesAngebot = keyof typeof BUYBOX_EIGENES_ANGEBOT;

/** Angebote auf einer Produktseite zusammenfassen: nach EAN (wie die PHP-Middleware) oder nach Merkmalen */
export const BUYBOX_GRUPPIERUNGEN = {
  ean: 'EAN',
  merkmale: 'Merkmalen',
} as const;
export type BuyBoxGruppierung = keyof typeof BUYBOX_GRUPPIERUNGEN;

export const BUYBOX_MERKMALE = {
  marke: 'Marke',
  modell: 'Modell',
  serie: 'Serie',
  farbe: 'Farbe',
  groesse: 'Größe',
  zustand: 'Zustand',
  /** Modell-, Bau- bzw. Erscheinungsjahr (z. B. Fahrrad, Bücher) – am Ende, damit bestehende Schlüssel gleich bleiben */
  jahr: 'Jahr',
} as const;
export type BuyBoxMerkmal = keyof typeof BUYBOX_MERKMALE;
export const BUYBOX_MERKMAL_LISTE = Object.keys(BUYBOX_MERKMALE) as BuyBoxMerkmal[];
export type BuyBoxMerkmalWerte = Partial<Record<BuyBoxMerkmal, string>>;

/** Vorlagen für die Pflichtmerkmale (Oberfläche) */
export const BUYBOX_MERKMAL_VORLAGEN: { titel: string; merkmale: BuyBoxMerkmal[] }[] = [
  { titel: 'Uhren: Marke, Modell (Referenz), Zifferblattfarbe, Gehäusegröße, Zustand', merkmale: ['marke', 'modell', 'farbe', 'groesse', 'zustand'] },
  { titel: 'Uhren streng: zusätzlich Serie', merkmale: ['marke', 'modell', 'serie', 'farbe', 'groesse', 'zustand'] },
  { titel: 'Schmuck: Marke, Modell, Material, Größe, Zustand', merkmale: ['marke', 'modell', 'farbe', 'groesse', 'zustand'] },
  { titel: 'Fashion: Marke, Modell, Farbe, Größe', merkmale: ['marke', 'modell', 'farbe', 'groesse'] },
  { titel: 'Fahrrad: Marke, Modell, Modelljahr, Rahmengröße, Zustand', merkmale: ['marke', 'modell', 'groesse', 'zustand', 'jahr'] },
  { titel: 'Elektronik (auch refurbished): Marke, Hersteller-Teilenummer, Zustand', merkmale: ['marke', 'modell', 'zustand'] },
  { titel: 'Autoteile: Marke, Hersteller-Teilenummer, Zustand', merkmale: ['marke', 'modell', 'zustand'] },
  { titel: 'Bücher: ISBN, Format, Zustand', merkmale: ['modell', 'serie', 'zustand'] },
];

export const BUYBOX_KRITERIEN = ['preis', 'bestand', 'bewertung', 'lieferzeit'] as const;
export type BuyBoxKriterium = (typeof BUYBOX_KRITERIEN)[number];
export type BuyBoxGewichte = Record<BuyBoxKriterium, number>;

/** Händler-ID des eigenen Angebots des Marktplatzes („Offizieller Shop“) */
export const BUYBOX_EIGEN = '__eigen';
/** Name der zusätzlichen Option im Shop */
export const BUYBOX_OPTION = 'Verkäufer';

/** Felder, die eine Kategorie abweichend festlegen kann */
export interface BuyBoxTeilregel {
  strategie?: BuyBoxStrategie;
  gewichte?: BuyBoxGewichte;
  preisInklVersand?: boolean;
  minBestand?: number;
  ohneBestandZulassen?: boolean;
  minBewertung?: number;
  gleichstand?: BuyBoxGleichstand;
  schwelle?: number;
  eigenesAngebot?: BuyBoxEigenesAngebot;
}

export interface BuyBoxAnzeige {
  weitere: boolean;
  max: number;
  bewertung: boolean;
  lieferzeit: boolean;
  versand: boolean;
}

export interface BuyBoxRegeln extends Required<BuyBoxTeilregel> {
  aktiv: boolean;
  bewertungQuelle: BuyBoxBewertungQuelle;
  /** Wonach Angebote auf einer Produktseite zusammengefasst werden */
  gruppierung: BuyBoxGruppierung;
  /** Pflichtmerkmale bei gruppierung = merkmale (müssen alle übereinstimmen) */
  merkmale: BuyBoxMerkmal[];
  /** Händler, die nie gewinnen (bleiben als „Weitere Angebote“ sichtbar) */
  ausgeschlossen: string[];
  anzeige: BuyBoxAnzeige;
}

export function buyBoxStandard(): BuyBoxRegeln {
  return {
    aktiv: true,
    strategie: 'preis',
    gewichte: { preis: 50, bestand: 10, bewertung: 25, lieferzeit: 15 },
    preisInklVersand: true,
    minBestand: 1,
    ohneBestandZulassen: true,
    minBewertung: 0,
    bewertungQuelle: 'kombiniert',
    gruppierung: 'ean',
    merkmale: [],
    gleichstand: 'bewertung',
    schwelle: 0,
    ausgeschlossen: [],
    eigenesAngebot: 'gleich',
    anzeige: { weitere: true, max: 5, bewertung: true, lieferzeit: true, versand: true },
  };
}

/* ---------------------------------------------------------------- Eingaben */

const Gewicht = z.number({ invalid_type_error: 'Bitte eine Zahl eingeben' }).int('Bitte eine ganze Zahl').min(0, 'Mindestens 0').max(100, 'Höchstens 100');
export const BuyBoxGewichteSchema = z.object({ preis: Gewicht, bestand: Gewicht, bewertung: Gewicht, lieferzeit: Gewicht });

const strategie = z.enum(BUYBOX_STRATEGIE_LISTE as [BuyBoxStrategie, ...BuyBoxStrategie[]]);
const gleichstand = z.enum(Object.keys(BUYBOX_GLEICHSTAND) as [BuyBoxGleichstand, ...BuyBoxGleichstand[]]);
const eigenesAngebot = z.enum(Object.keys(BUYBOX_EIGENES_ANGEBOT) as [BuyBoxEigenesAngebot, ...BuyBoxEigenesAngebot[]]);
const minBestand = z.number({ invalid_type_error: 'Bitte eine Zahl eingeben' }).int('Bitte eine ganze Zahl').min(0, 'Mindestens 0').max(100_000, 'Höchstens 100.000');
const minBewertung = z.number({ invalid_type_error: 'Bitte eine Zahl eingeben' }).min(0, 'Mindestens 0').max(5, 'Höchstens 5 Sterne');
const schwelle = z.number({ invalid_type_error: 'Bitte eine Zahl eingeben' }).min(0, 'Mindestens 0 %').max(50, 'Höchstens 50 %');

export const BuyBoxTeilregelSchema = z
  .object({
    strategie: strategie.optional(),
    gewichte: BuyBoxGewichteSchema.optional(),
    preisInklVersand: z.boolean().optional(),
    minBestand: minBestand.optional(),
    ohneBestandZulassen: z.boolean().optional(),
    minBewertung: minBewertung.optional(),
    gleichstand: gleichstand.optional(),
    schwelle: schwelle.optional(),
    eigenesAngebot: eigenesAngebot.optional(),
  })
  .strict();

export const BuyBoxAnzeigeSchema = z.object({
  weitere: z.boolean(),
  max: z.number().int().min(1, 'Mindestens 1').max(20, 'Höchstens 20'),
  bewertung: z.boolean(),
  lieferzeit: z.boolean(),
  versand: z.boolean(),
});

const merkmal = z.enum(BUYBOX_MERKMAL_LISTE as [BuyBoxMerkmal, ...BuyBoxMerkmal[]]);

/** Regeln des Marktplatzes (ohne Kategorie-Ausnahmen, die haben eigene Einträge) */
export const BuyBoxRegelnSchema = z.object({
  aktiv: z.boolean(),
  strategie,
  gewichte: BuyBoxGewichteSchema,
  preisInklVersand: z.boolean(),
  minBestand,
  ohneBestandZulassen: z.boolean(),
  minBewertung,
  bewertungQuelle: z.enum(['manuell', 'leistung', 'kombiniert']),
  gruppierung: z.enum(['ean', 'merkmale']),
  merkmale: z.array(merkmal).max(BUYBOX_MERKMAL_LISTE.length).transform((a) => BUYBOX_MERKMAL_LISTE.filter((m) => a.includes(m))),
  gleichstand,
  schwelle,
  ausgeschlossen: z.array(z.string().uuid()).max(1000, 'Höchstens 1.000 ausgeschlossene Händler').transform((a) => [...new Set(a)]),
  eigenesAngebot,
  anzeige: BuyBoxAnzeigeSchema,
}).refine((r) => r.gruppierung !== 'merkmale' || r.merkmale.length > 0, { message: 'Bitte mindestens ein Merkmal wählen', path: ['merkmale'] });

const Kategorie = z.string().trim().min(1, 'Bitte eine Kategorie angeben').max(120);

/** regel = null entfernt die Ausnahme */
export const BuyBoxKategorieSchema = z.object({ kategorie: Kategorie, regel: BuyBoxTeilregelSchema.nullable() });
export type BuyBoxKategorieEingabe = z.infer<typeof BuyBoxKategorieSchema>;

const Zahl = (min: number, max: number, text: string) => z.number({ invalid_type_error: 'Bitte eine Zahl eingeben' }).min(min, `Mindestens ${min}`).max(max, text);

/** Buy-Box-Angaben eines Händlers bzw. des eigenen Angebots (bb_haendler_speichern / bb_eigen_speichern) */
export const BuyBoxWerteSchema = z
  .object({
    anzeigename: z.string().trim().max(60, 'Höchstens 60 Zeichen'),
    aktiv: z.boolean(),
    bewertung: Zahl(0, 5, 'Höchstens 5 Sterne').multipleOf(0.01, 'Höchstens zwei Nachkommastellen').nullable(),
    bewertungen: z.number().int('Bitte eine ganze Zahl').min(0, 'Mindestens 0').max(10_000_000),
    versand: Zahl(0, 999, 'Höchstens 999 €').multipleOf(0.01, 'Höchstens zwei Nachkommastellen'),
    versandfreiAb: Zahl(0, 99_999, 'Höchstens 99.999 €').multipleOf(0.01, 'Höchstens zwei Nachkommastellen').nullable(),
    lieferzeitMin: z.number().int('Bitte ganze Tage').min(0, 'Mindestens 0').max(60, 'Höchstens 60 Tage'),
    lieferzeitMax: z.number().int('Bitte ganze Tage').min(0, 'Mindestens 0').max(90, 'Höchstens 90 Tage'),
  })
  .refine((w) => w.lieferzeitMax >= w.lieferzeitMin, { message: 'Darf nicht kürzer als die kürzeste Lieferzeit sein', path: ['lieferzeitMax'] });
export type BuyBoxWerte = z.infer<typeof BuyBoxWerteSchema>;

export const BuyBoxHaendlerAbfrage = z.object({
  q: z.string().trim().max(100).optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

export const BuyBoxVorschauSchema = z
  .object({
    produkt: z.string().uuid().optional(),
    ean: z.string().trim().max(40).optional(),
    /** ungespeicherte Regeln ausprobieren (wie bb_get mit „regeln“) */
    regeln: BuyBoxRegelnSchema.optional(),
    kategorien: z.record(Kategorie, BuyBoxTeilregelSchema).optional(),
  })
  .refine((a) => a.produkt || a.ean, 'Bitte ein Produkt oder eine EAN angeben');
export type BuyBoxVorschauEingabe = z.infer<typeof BuyBoxVorschauSchema>;

/* ---------------------------------------------------------------- Antworten */

export interface BuyBoxLeistung {
  bestellungen: number;
  versendet: number;
  storno: number;
  /** Ø Tage bis zum Versand */
  versanddauer: number | null;
  /** % versendet innerhalb von 2 Tagen */
  puenktlich: number | null;
}

export interface BuyBoxHaendlerZeile {
  id: string;
  firma: string;
  code: string | null;
  status: string;
  /** gepflegte Angaben; Standardwerte, solange nichts gespeichert ist */
  werte: BuyBoxWerte;
  gespeichert: boolean;
  /** 0–5 Sterne aus Bestellungen der letzten 180 Tage (ab 3 Bestellungen) */
  bewertungLeistung: number | null;
  /** wirksame Bewertung nach der Quelle aus den Regeln */
  bewertung: number | null;
  leistung: BuyBoxLeistung;
  ausgeschlossen: boolean;
}

export interface BuyBoxHaendlerSeite {
  eintraege: BuyBoxHaendlerZeile[];
  weiter: string | null;
}

export interface BuyBoxKategorieZeile {
  kategorie: string;
  regel: BuyBoxTeilregel;
}

export interface BuyBoxUebersicht {
  regeln: BuyBoxRegeln;
  kategorien: BuyBoxKategorieZeile[];
  eigen: BuyBoxWerte;
  /** Kategorien mit Produkten (Auswahl für Ausnahmen) */
  produktKategorien: string[];
  ausgeschlossen: { id: string; firma: string }[];
}

export interface BuyBoxZeile {
  id: string;
  produktId: string | null;
  haendlerId: string;
  haendler: string;
  eigen: boolean;
  titel: string;
  sku: string;
  variante: string;
  preis: number;
  vergleichspreis: number;
  versand: number;
  versandfreiAb: number | null;
  bestand: number | null;
  bewertung: number | null;
  bewertungen: number;
  lieferzeitMin: number;
  lieferzeitMax: number;
  status: string;
  gelistet: boolean;
  /** Variante im Marktplatz-Shop (nur Ziffern), leer solange nicht übertragen */
  shopVarianteId: string;
  angelegt: string;
  zulaessig: boolean;
  ausschluss: string[];
  score: number;
  teilwerte: BuyBoxGewichte;
  rang: number;
  gewinner: boolean;
}

export interface BuyBoxGroesse {
  /** Gruppe: vereinheitlichte EAN bzw. Merkmalsschlüssel */
  schluessel: string;
  ean: string;
  /** fehlende Pflichtmerkmale – das Angebot steht dann allein */
  fehlend: BuyBoxMerkmal[];
  /** z. B. „Merkmal fehlt: Serie, Zustand“ */
  hinweis: string;
  label: string;
  werte: Record<string, string>;
  gewinner: string;
  grund: string;
  strategie: BuyBoxStrategie;
  strategieLabel: string;
  kategorie: string;
  /** abweichende Regel der Kategorie greift */
  kategorieRegel: boolean;
  liste: BuyBoxZeile[];
}

export interface BuyBoxVorschau {
  gruppierung: BuyBoxGruppierung;
  titel: string;
  kategorie: string;
  eans: string[];
  groessen: BuyBoxGroesse[];
  /** Option „Verkäufer“ je Angebot */
  verkaeufer: Record<string, string>;
  /** Inhalt des Produkt-Metafelds dxl_marketplace.offers */
  metafeld: unknown;
  gekuerzt: boolean;
  /** Stand im Marktplatz-Shop, wenn die Angebote als ein Produkt (Sammelprodukt bzw. eigenes Produkt) dort stehen */
  modell: BuyBoxModellZeile | null;
}

/** Ein Shop-Produkt je Modell (Sammelprodukt bzw. eigenes Produkt des Marktplatzes) – bb_modelle */
export interface BuyBoxModellZeile {
  id: string;
  titel: string;
  kategorie: string;
  /** EANs bzw. Merkmalsschlüssel */
  gruppen: string[];
  shopProduktId: string | null;
  handle: string | null;
  /** Händler-Angebote stehen im eigenen Produkt des Marktplatzes */
  eigen: boolean;
  angebote: number;
  produkte: number;
  /** offen: noch nicht übertragen · ok · fehler: letzter Abgleich fehlgeschlagen (wird wiederholt) */
  stand: 'offen' | 'ok' | 'fehler';
  fehler: string;
  hinweis: string;
  uebertragenAm: string | null;
  /** letzte Gewinnerwechsel, neueste zuerst */
  wechsel: { zeit: string; gruppe: string; von: string; zu: string; grund: string }[];
}

export interface BuyBoxModellSeite {
  eintraege: BuyBoxModellZeile[];
  weiter: string | null;
  /** Domain des Marktplatz-Shops (Verweis in die Shop-Verwaltung) */
  shop: string | null;
}

/* ---------------------------------------------------------------- Hilfen für die Oberfläche */

export function buyBoxLieferzeitText(min: number, max: number): string {
  return min === max ? `${min} Werktage` : `${min}–${max} Werktage`;
}
